<template>
  <div class="cdr-layout">
    <Sidebar />
    <div class="main-content">
      <TopHeader pageTitle="Call History" />
      <main class="page-content">

        <!-- Page Header -->
        <div class="page-header">
          <div class="header-content">
            <h1>Call History</h1>
            <p class="subtitle">
              FreePBX call detail records synced from Asterisk.
              <span v-if="lastSync" class="sync-badge">Last sync: {{ relativeTime(lastSync) }}</span>
            </p>
          </div>
          <div class="header-actions">
            <button v-if="hasActiveFilters" class="btn-clear" @click="clearFilters">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
              Clear filters
            </button>
            <button class="btn-secondary" @click="load" :disabled="loading">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
              Refresh
            </button>
          </div>
        </div>

        <!-- Filters -->
        <div class="filter-panel">
          <div class="filter-row">
            <!-- Search -->
            <div class="search-wrap">
              <svg class="search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input v-model="filters.search" class="filter-input search-input" placeholder="Search number..." @input="debouncedLoad" />
            </div>
            <!-- Extension -->
            <div class="filter-field">
              <input v-model="filters.extension" class="filter-input" placeholder="Extension (exact)" @input="debouncedLoad" />
            </div>
            <!-- Disposition -->
            <select v-model="filters.disposition" class="filter-select" @change="resetAndLoad">
              <option value="">All dispositions</option>
              <option value="ANSWERED">Answered</option>
              <option value="NO ANSWER">No Answer</option>
              <option value="BUSY">Busy</option>
              <option value="FAILED">Failed</option>
            </select>
            <!-- Direction -->
            <select v-model="filters.direction" class="filter-select" @change="resetAndLoad">
              <option value="">All directions</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>
          <div class="filter-row">
            <!-- Date From -->
            <div class="filter-field date-field">
              <label class="filter-label">From</label>
              <input v-model="filters.date_from" type="date" class="filter-input" @change="resetAndLoad" />
            </div>
            <!-- Date To -->
            <div class="filter-field date-field">
              <label class="filter-label">To</label>
              <input v-model="filters.date_to" type="date" class="filter-input" @change="resetAndLoad" />
            </div>
            <!-- Sort -->
            <div class="sort-toggle">
              <button
                class="sort-btn"
                :class="{ active: filters.sort === 'desc' }"
                @click="setSort('desc')"
                title="Newest first"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 17H22L18 21L14 17H17V3H19V17ZM11 13H3V11H11V13ZM9 7V9H3V7H9ZM3 17H7V15H3V17Z"/></svg>
                Newest
              </button>
              <button
                class="sort-btn"
                :class="{ active: filters.sort === 'asc' }"
                @click="setSort('asc')"
                title="Oldest first"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 7H22L18 3L14 7H17V21H19V7ZM11 13H3V11H11V13ZM9 7V9H3V7H9ZM3 17H7V15H3V17Z"/></svg>
                Oldest
              </button>
            </div>
            <!-- Active filter count -->
            <div class="filter-summary" v-if="total > 0">
              {{ total }} record{{ total !== 1 ? 's' : '' }}
            </div>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="error-state">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
          <div>
            <div>{{ error }}</div>
            <button class="retry-btn" @click="load">Retry</button>
          </div>
        </div>

        <div v-else>
          <!-- Table -->
          <div class="table-card">
            <table class="data-table" v-if="records.length">
              <thead>
                <tr>
                  <th></th>
                  <th>Caller</th>
                  <th>Destination</th>
                  <th>Duration</th>
                  <th>Answered</th>
                  <th>Disposition</th>
                  <th>Date / Time</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rec in records" :key="rec.uniqueid">
                  <td class="direction-cell">
                    <span class="direction-icon" :class="directionClass(rec)" :title="directionLabel(rec)">
                      <svg v-if="inferDirection(rec) === 'inbound'" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 5.41L18.59 4 7 15.59V9H5v10h10v-2H8.41z"/></svg>
                      <svg v-else-if="inferDirection(rec) === 'outbound'" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 5v2h6.59L4 18.59 5.41 20 17 8.41V15h2V5z"/></svg>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.24l-2.2 2.2c-2.83-1.44-5.15-3.75-6.59-6.59l2.2-2.21c.28-.26.36-.65.25-1C8.7 6.45 8.5 5.25 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"/></svg>
                    </span>
                  </td>
                  <td>
                    <span class="phone-number">{{ rec.src }}</span>
                  </td>
                  <td>
                    <span class="phone-number">{{ rec.dst }}</span>
                  </td>
                  <td class="muted-cell">{{ formatDuration(rec.duration) }}</td>
                  <td class="muted-cell">{{ rec.billsec > 0 ? formatDuration(rec.billsec) : '—' }}</td>
                  <td>
                    <span class="disposition-badge" :class="'disp-' + dispositionKey(rec.disposition)">
                      <span class="disp-dot"></span>
                      {{ rec.disposition }}
                    </span>
                  </td>
                  <td class="muted-cell">{{ formatDate(rec.calldate) }}</td>
                </tr>
              </tbody>
            </table>

            <div v-else class="empty-state">
              <div class="empty-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
              </div>
              <h3>No call records found</h3>
              <p>{{ hasActiveFilters ? 'Try adjusting your filters.' : 'Call records will appear here once FreePBX syncs.' }}</p>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination" v-if="totalPages > 1">
            <button class="page-btn" :disabled="page <= 1" @click="changePage(page - 1)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>
              Previous
            </button>
            <span class="page-info">Page {{ page }} of {{ totalPages }} <span class="total-count">({{ total }} records)</span></span>
            <button class="page-btn" :disabled="page >= totalPages" @click="changePage(page + 1)">
              Next
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
            </button>
          </div>
        </div>

      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import { IVRService } from '@/services/ivrService'

let debounceTimer = null

export default {
  name: 'CallHistoryView',
  components: { Sidebar, TopHeader },

  data () {
    return {
      records: [],
      total: 0,
      page: 1,
      totalPages: 1,
      lastSync: null,
      loading: true,
      error: null,
      filters: {
        search: '',
        extension: '',
        disposition: '',
        direction: '',
        date_from: '',
        date_to: '',
        sort: 'desc'
      }
    }
  },

  computed: {
    hasActiveFilters () {
      const f = this.filters
      return f.search || f.extension || f.disposition || f.direction || f.date_from || f.date_to || f.sort !== 'desc'
    }
  },

  async mounted () { await this.load() },

  methods: {
    async load () {
      this.loading = true
      this.error = null
      try {
        const params = { page: this.page, per_page: 50, sort: this.filters.sort }
        if (this.filters.search) params.search = this.filters.search
        if (this.filters.extension) params.extension = this.filters.extension
        if (this.filters.disposition) params.disposition = this.filters.disposition
        if (this.filters.direction) params.direction = this.filters.direction
        if (this.filters.date_from) params.date_from = this.filters.date_from
        if (this.filters.date_to) params.date_to = this.filters.date_to
        const res = await IVRService.getCdrHistory(params)
        this.records = res.records || []
        this.total = res.total || 0
        this.page = res.page || 1
        this.totalPages = Math.ceil((res.total || 0) / (res.per_page || 50))
        this.lastSync = res.last_sync || null
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },

    debouncedLoad () {
      this.page = 1
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => this.load(), 400)
    },

    resetAndLoad () {
      this.page = 1
      this.load()
    },

    setSort (val) {
      this.filters.sort = val
      this.page = 1
      this.load()
    },

    clearFilters () {
      this.filters = { search: '', extension: '', disposition: '', direction: '', date_from: '', date_to: '', sort: 'desc' }
      this.page = 1
      this.load()
    },

    changePage (p) {
      this.page = p
      this.load()
    },

    inferDirection (rec) {
      if (rec.src && rec.src.length > 6) return 'inbound'
      if (rec.dst && rec.dst.length > 6) return 'outbound'
      return 'internal'
    },

    directionClass (rec) {
      const dir = this.inferDirection(rec)
      const map = { inbound: 'dir-inbound', outbound: 'dir-outbound', internal: 'dir-internal' }
      return map[dir]
    },

    directionLabel (rec) {
      const dir = this.inferDirection(rec)
      const map = { inbound: 'Inbound', outbound: 'Outbound', internal: 'Internal' }
      return map[dir]
    },

    dispositionKey (disp) {
      const map = {
        ANSWERED: 'answered',
        'NO ANSWER': 'noanswer',
        BUSY: 'busy',
        FAILED: 'failed'
      }
      return map[disp] || 'unknown'
    },

    formatDuration (secs) {
      if (!secs && secs !== 0) return '—'
      const m = Math.floor(secs / 60)
      const s = secs % 60
      return m > 0 ? `${m}m ${s}s` : `${s}s`
    },

    formatDate (str) {
      if (!str) return '—'
      return new Date(str).toLocaleString()
    },

    relativeTime (dateStr) {
      if (!dateStr) return 'never'
      const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
      if (diff < 60) return 'just now'
      if (diff < 3600) return Math.floor(diff / 60) + ' min ago'
      if (diff < 86400) return Math.floor(diff / 3600) + ' hr ago'
      return Math.floor(diff / 86400) + ' d ago'
    }
  }
}
</script>

<style scoped>
.cdr-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}
.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}
.page-content {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
}

/* Page Header */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}
.header-content h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}
.subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.sync-badge {
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  padding: 2px 10px;
  border-radius: 20px;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.btn-clear {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.btn-clear:hover { background: var(--bg-tertiary); color: var(--text-primary); }

/* Filter Panel */
.filter-panel {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.filter-row {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 160px;
}
.search-icon {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
}
.search-input { padding-left: 34px !important; }

.filter-field {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 130px;
}
.date-field { min-width: 170px; flex: 0 0 auto; }
.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  white-space: nowrap;
}
.filter-input {
  flex: 1;
  padding: 9px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-primary);
  color: var(--text-primary);
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  width: 100%;
}
.filter-input:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}
.filter-select {
  padding: 9px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.2s;
  flex-shrink: 0;
}
.filter-select:focus { outline: none; border-color: #3B82F6; }

/* Sort Toggle */
.sort-toggle {
  display: flex;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}
.sort-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  background: var(--bg-primary);
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  transition: all 0.2s;
}
.sort-btn:first-child { border-right: 1px solid var(--border-primary); }
.sort-btn:hover { background: var(--bg-tertiary); color: var(--text-primary); }
.sort-btn.active { background: rgba(59,130,246,0.08); color: #3B82F6; }

.filter-summary {
  font-size: 13px;
  color: var(--text-secondary);
  margin-left: auto;
  white-space: nowrap;
}

/* Loading / Error / Empty */
.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}
.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.error-state {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: 12px;
  padding: 18px 20px;
  color: #DC2626;
  font-size: 14px;
}
.retry-btn {
  margin-top: 8px;
  padding: 6px 14px;
  border: 1px solid #FECACA;
  border-radius: 6px;
  background: #FEF2F2;
  color: #DC2626;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
}
.retry-btn:hover { background: #FEE2E2; }

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 20px;
  text-align: center;
}
.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;
}
.empty-state h3 { font-size: 18px; font-weight: 600; color: var(--text-primary); margin: 0; }
.empty-state p { font-size: 14px; color: var(--text-secondary); margin: 0; }

/* Table */
.table-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  overflow: hidden;
}
.data-table { width: 100%; border-collapse: collapse; }
.data-table th {
  background: var(--bg-tertiary);
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid var(--border-primary);
}
.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-primary);
  font-size: 14px;
  color: var(--text-primary);
}
.data-table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover { background: var(--bg-tertiary); }

/* Direction */
.direction-cell { width: 36px; padding-right: 0 !important; }
.direction-icon {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dir-inbound { background: rgba(16,185,129,0.1); color: #10B981; }
.dir-outbound { background: rgba(59,130,246,0.1); color: #3B82F6; }
.dir-internal { background: var(--bg-tertiary); color: var(--text-secondary); }

.phone-number {
  font-family: monospace;
  font-weight: 600;
  font-size: 14px;
}
.muted-cell { color: var(--text-secondary); font-size: 13px; }

/* Disposition badges */
.disposition-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
}
.disp-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.disp-answered { background: rgba(16,185,129,0.1); color: #059669; }
.disp-answered .disp-dot { background: #10B981; }
.disp-noanswer { background: var(--bg-tertiary); color: var(--text-secondary); border: 1px solid var(--border-primary); }
.disp-noanswer .disp-dot { background: #9CA3AF; }
.disp-busy { background: rgba(245,158,11,0.1); color: #D97706; }
.disp-busy .disp-dot { background: #F59E0B; }
.disp-failed { background: rgba(239,68,68,0.1); color: #DC2626; }
.disp-failed .disp-dot { background: #EF4444; }
.disp-unknown { background: var(--bg-tertiary); color: var(--text-secondary); }
.disp-unknown .disp-dot { background: #9CA3AF; }

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 20px;
}
.page-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.page-btn:hover:not(:disabled) { background: var(--bg-tertiary); border-color: var(--border-secondary); }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 14px; color: var(--text-secondary); }
.total-count { font-size: 12px; color: var(--text-secondary); }

/* Buttons */
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  transition: all 0.2s;
}
.btn-secondary:hover:not(:disabled) { background: var(--bg-tertiary); }
.btn-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
